import { pricingCalculator } from 'components/common/VendorPricingCalc';
import { PROGRAMS_MEQ } from 'components/common/vendorConfig';
import type { Bundle, Observation, Patient } from 'fhir/r4';
import _ from 'lodash';
import moment from 'moment';
import { useEffect, useState } from 'react';
import React from 'react';
import { DatesRangeInput } from 'semantic-ui-calendar-react';
import { Loader, Message, Icon, Button, Form, Confirm } from 'semantic-ui-react';
import type { InputOnChangeData, StrictCheckboxProps } from 'semantic-ui-react';
import useSWR from 'swr';
import type { SWRConfiguration } from 'swr';
import type {
  Fetched,
  FlatPatient,
  FoodItem,
  FoodOrder,
  OrderNote,
  PatientObservationsByCode,
  PatientObservationsById,
  ProgramBudget,
  ReferralSummary,
  VendorPriceRow,
  VendorPricingByCode,
} from 'types';
import { mapKey } from 'utils';

import { useAuthContext } from '../../../../contexts/AuthContext';
import { MultiFetcher } from '../../../../data/FHIRFetcher';
import FoodDelete from '../../../../data/FoodDelete';
import FoodFetcher, { MultiFoodFetcher } from '../../../../data/FoodFetcher';
import FoodUpdater from '../../../../data/FoodUpdater';
import * as FHIRUtils from '../../../../utils/FHIRUtils';
import { calculateMealEquivalents } from '../utils/foodUtils';

import { statusColors } from './constants';
import DraftOrdersTable from './table';

/**
 * The sort-and-filter state behind the draft-orders table. Structurally the same
 * as `../orders/index.tsx`'s, but kept local: the two reducers handle different
 * action sets — this one has no `UPDATE` arm.
 */
type DraftOrdersTableState = {
  // `null` from the seed, `undefined` from SWR's `data`: both really occur.
  data: FoodOrder[] | null | undefined;
  direction: 'ascending' | 'descending' | null;
  sortcolumn: DraftOrdersTableAction['sortcolumn'] | null;
};

/** Every action this reducer is dispatched, from the four `dispatch({...})` calls below. */
type DraftOrdersTableAction = {
  data?: FoodOrder[] | null | undefined;
  datevalue?: string | null;
  /**
   * A column NAME from the table's sort headers, or the lodash ITERATEE FUNCTION the
   * post-load `CHANGE_SORT` in `draftOrdersOptions.onSuccess` passes
   * (`(od) => [statusColors[mapKey(od.status)], od.orderDate]`).
   *
   * Spelling it `string | ((order: FoodOrder) => unknown)` type-checks the field but
   * not the call: that union matches neither `sortBy` overload cleanly, so resolution
   * falls to the `T extends object` one and the result element type becomes
   * `FoodOrder[keyof FoodOrder]`.
   *
   * Unlike the twin in `../orders/index.tsx` — which subscripts by this value and so
   * stringifies a function into a property name — this reducer passes it straight to
   * lodash, which accepts a function iteratee. The default sort genuinely works here.
   * The two reducers disagree on this one point; it is not a copy error to undo.
   *
   * SUSPECTED DEFECT — REPORTED, NOT FIXED. The `state.sortcolumn === action.sortcolumn`
   * reverse-sort test above compares by reference, and `onSuccess` builds a FRESH
   * closure on every response. So a re-fetch never takes the reverse branch even when
   * the sort is unchanged; it re-sorts ascending instead. Harmless for the post-load
   * dispatch (ascending is what it wants) but it means the state field can hold a
   * function that no header click can ever match.
   */
  sortcolumn?: _.ListIteratee<FoodOrder>;
  type: string;
};

/**
 * SUSPECTED DEFECT — REPORTED, NOT FIXED. The twin of the reducer in
 * `../orders/index.tsx`, with the same three absences: `useReducer` seeds `data`
 * as `null`, so `state.data.slice()` throws on a `CHANGE_SORT` before any dataset
 * arrives while `_.sortBy(state.data, …)` in the sibling arm silently returns
 * `[]`, and `FILTER` dereferences an `action.data` its own type marks optional.
 *
 * Unreachable today because every dispatch site passes an array and the sort
 * headers live inside a table that only mounts once data exists.
 */
function Reducer(
  state: DraftOrdersTableState,
  action: DraftOrdersTableAction,
): DraftOrdersTableState {
  switch (action.type) {
    case 'CHANGE_SORT':
      if (state.sortcolumn === action.sortcolumn) {
        return {
          ...state,
          data: state.data!.slice().reverse(),
          direction: state.direction === 'ascending' ? 'descending' : 'ascending',
        };
      }
      return {
        sortcolumn: action.sortcolumn,
        // Asserted, not defaulted: an `undefined` in this array steers overload
        // resolution onto `sortBy(T extends object, …)`, which returns the wrong
        // element type rather than reporting the absence.
        data: _.sortBy(state.data, [action.sortcolumn!]),
        direction: 'ascending',
      };
    case 'FILTER':
      return {
        ...state,
        data: action.data!.filter((d) => {
          return (
            moment(d.orderDate).local().format('YYYY-MM-DD') ===
            moment(action.datevalue).format('YYYY-MM-DD')
          );
        }),
      };
    case 'CLEAR_FILTERS':
      return {
        ...state,
        data: action.data,
        sortcolumn: null,
      };
    default:
      throw new Error();
  }
}
/**
 * What the draft-orders screen accepts. Two JSX call sites in `../index.tsx`,
 * splitting on `patientId` as the orders screen's do.
 *
 * Most of what those call sites pass is never read here. The budget and
 * meal-equivalent family (`ordersMealEquivalents`, `setOrdersMealEquivalents`,
 * `programOrdersCost`, `setProgramOrdersCost`, `programMAXBudget`,
 * `programMAXMealEquivalents`, `programBudget`, `setProgramBudget`,
 * `allReferralData`) is consumed only by the sibling `../orders`; `profileData`,
 * `patient` and `vendorName` are likewise inert. Declared so the call sites keep
 * compiling — the two screens are fed identically while only one uses the feed.
 */
type DraftOrdersProps = {
  /** Accepted but unread. */
  allReferralData?: ReferralSummary[];
  /**
   * Per-org contracted meal-equivalent ceilings, keyed by `patientOrg`. Indexed
   * with a raw string and compared with `parseInt`, so the values arrive as
   * strings even though they are counts.
   */
  contractsME?: Record<string, string | number>;
  /** Opens the roster-wide member drawer; called unguarded. */
  onPatientSelected?: (patient: unknown) => void;
  /** Accepted but unread. */
  ordersMealEquivalents?: number;
  /**
   * The member, already flattened. Read only to source `patient.org` for the
   * table's `org` prop — which the table itself does not read.
   */
  patient?: FlatPatient;
  /** Absent or null selects the roster-wide mode. */
  patientId?: string | null;
  /** Accepted but unread. */
  profileData?: Bundle | Record<string, never>;
  /** Accepted but unread. */
  programBudget?: ProgramBudget[];
  /** Accepted but unread. */
  programMAXBudget?: number;
  /** Accepted but unread. */
  programMAXMealEquivalents?: number;
  /** Accepted but unread. */
  programOrdersCost?: number;
  /** The org filter for the roster-wide mode. */
  selectedOrg?: string;
  /** Accepted but unread. */
  setOrdersMealEquivalents?: (mealEquivalents: number) => void;
  /** Accepted but unread. */
  setProgramBudget?: (budget: ProgramBudget[]) => void;
  /** Accepted but unread. */
  setProgramOrdersCost?: (cost: number) => void;
  /** A vendor code, or the literal 'All'. */
  vendor?: string;
  /** Accepted but unread. */
  vendorName?: string;
};

function DraftOrders(props: DraftOrdersProps) {
  const authInfo = useAuthContext();
  const practitionerName = authInfo.practitionerName;
  // Seeded `''` but `resetFilters` writes `null`; only read for truthiness, which is
  // why the two producers disagreeing has never shown.
  const [statusvalue, setStatusValue] = useState<string | null>('');
  const [dateValue, setDateValue] = useState<string | null>(null);
  // `undefined` comes from `selectAll` — see the SUSPECTED DEFECT there.
  const [selectedOrderIds, setselectedOrderIds] = useState<(string | undefined)[]>([]);
  const [selectedOrdersLength, setselectedOrdersLength] = useState(0);
  const [ordersMessage, setOrdersMessage] = useState('');
  const [working, setWorking] = useState(false);
  const [workingon, setWorkingOn] = useState('');
  const [hideMessage, setHideMessage] = useState(false);
  const [_open, _setOpen] = useState(false);
  const [orderDetails, setOrderDetails] = useState<Record<string, unknown>>({});
  const [searchId, setSearchId] = useState('');
  const [_statusOptions, setStatusOptions] = useState<unknown[] | null>(null);
  const [dateState, setdateState] = useState('');
  const [startRange, setStartRange] = useState('');
  const [endRange, setEndRange] = useState('');
  const [_sendConfirm, _setSendConfirm] = useState(false);
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const [ordersUpdateError, setOrdersUpdateError] = useState(false);
  // Both are maps keyed by patient id, accumulated across the paged /Patient fetches below.
  const [rawPatients, setRawPatients] = useState<Record<string, unknown> | null>(null);
  const [patients, setPatients] = useState<Record<string, FlatPatient> | null>(null);
  const [patientObservations, setPatientObservations] = useState<PatientObservationsById>({});

  const [_bundleItemsData, setBundleItemsData] = useState<FoodItem[] | null | undefined>(null);
  const [_itemsData, setItemsData] = useState<FoodItem[] | null | undefined>(null);
  const [_vendorsFoodPrice, setVendorsFoodPrice] = useState<VendorPricingByCode>({});

  const _PATIENT_ATTRIBUTES = [
    'firstName',
    'lastName',
    'delivery_addressLine1',
    'delivery_addressLine2',
    'delivery_city',
    'delivery_state',
    'delivery_zip',
    'email',
    'homePhone',
  ];
  const [loading, setLoading] = React.useState(true);

  const invoiceStatusList = ['partially-fulfilled', 'unfulfilled', 'delivered', 'reshipped'];
  // Annotated because SWR 0.5.7 defaults `Data` to `any` and a detached options
  // `const` infers nothing from its `useSWR` call site.
  const draftOrdersOptions: SWRConfiguration<Fetched<FoodOrder[]>> = {
    shouldRetryOnError: false,
    revalidateOnFocus: false,
    onSuccess: (data) => {
      if (!statusvalue && !dateValue) {
        setStatusOptions(
          Array.from(
            new Set(
              data &&
                data.map((order) => {
                  return order.status;
                }),
            ),
          ),
        );
      }
      setLoading(false);
      dispatch({ type: 'CLEAR_FILTERS', data: data });
      dispatch({
        type: 'CHANGE_SORT',
        sortcolumn: (od) => {
          return [statusColors[mapKey(od.status)], od.orderDate];
        },
        data: data,
      });
      if (data && data.length) {
        const patientsObj = data.map((order) => {
          return { id: order.patientId, org: order.patientOrg };
        });
        // The `filter(c => c && …)` below drops the absent ids before they are joined
        // into the search URLs.
        const orgPatients: Record<string, (string | undefined)[]> = {};
        patientsObj.forEach((patient) => {
          if (patient.org) {
            if (Object.prototype.hasOwnProperty.call(orgPatients, patient.org)) {
              // The `hasOwnProperty` test above is what makes this read safe;
              // noUncheckedIndexedAccess cannot see through it.
              orgPatients[patient.org]!.push(patient.id);
            } else {
              orgPatients[patient.org] = [patient.id];
            }
          }
        });
        const urls: string[] = [];
        const observationsUrl: string[] = [];
        if (Object.keys(orgPatients).length) {
          Object.keys(orgPatients).forEach((org) => {
            const patients = orgPatients[org]!;
            const uniquePatients = patients.filter((c, index) => {
              if (c && c !== '') return patients.indexOf(c) === index;
            });
            if (uniquePatients && uniquePatients.length) {
              const groups = uniquePatients
                .map((e, i) => {
                  return i % 50 === 0 ? uniquePatients.slice(i, i + 50) : null;
                })
                .filter((e): e is (string | undefined)[] => Boolean(e));
              // eslint-disable-next-line @typescript-eslint/no-for-in-array -- `for...in` over an array is the existing traversal; changing it to `for...of` would alter the index type from string to element and is a behaviour change, not an annotation. The rule became visible only once `groups` stopped being `any`.
              for (const groupIndex in groups) {
                urls.push(`/Patient/?organization=${org}&_id=${groups[groupIndex]!.join(',')}`);
                observationsUrl.push(
                  `/Observation?patient=${groups[groupIndex]!.join(
                    ',',
                  )}&code=ALLERGIES,FOOD-PREFERENCES,DIAGNOSIS,FOOD-AVOIDANCE`,
                );
              }
            }
          });
          getPatients(urls);
          getPatientObservations(observationsUrl);
        } else {
          setLoading(false);
        }
      }
    },
  };

  const getPatientObservations = (urls: string[]) => {
    MultiFetcher<Bundle>(authInfo.token, ...urls).then(
      (observationsDataset) => {
        const patientObservations: PatientObservationsById = {};
        /*
         * SUSPECTED DEFECT — REPORTED, NOT FIXED. `MultiFetcher` resolves `undefined`,
         * not `[]`, when handed no URLs, so this `.forEach` throws on that arm.
         *
         * The arm IS reachable, contrary to the note previously here claiming the
         * caller only passes a non-empty `urls`: the caller's gate is
         * `Object.keys(orgPatients).length`, while `observationsUrl` is only pushed to
         * under the inner `uniquePatients.length` test. A roster whose every patient id
         * is absent or `''` passes the outer gate and pushes nothing.
         *
         * Asserted rather than optional-chained: `!` erases, so the throw is preserved
         * exactly. A `?.` would convert it into a silent no-op.
         */
        observationsDataset!.forEach((observationsData) => {
          if (observationsData && observationsData.entry && observationsData.entry.length) {
            observationsData.entry.forEach((rawObservation) => {
              const observation = FHIRUtils.parseFHIRPreference(
                rawObservation.resource as Observation,
              );
              /*
               * SUSPECTED DEFECT — REPORTED, NOT FIXED. `parseFHIRPreference`
               * does not produce a `patientId`: `FlatPreference` has no such
               * field and the parser never assigns one — unlike its siblings
               * `parseFHIRReferral` and `parseFHIRTask`, which both derive one
               * from `subject.reference`.
               *
               * So every bucket here is keyed by `undefined`, which the
               * subscript coerces to the single string key `"undefined"`: every
               * member's allergies, preferences, avoidances and diagnoses are
               * merged into ONE bucket shared by the whole roster, and no real
               * patient id can ever address it.
               *
               * LATENT today only because `draftOrders/table.tsx` declares
               * `patientObservations` and never reads it — so the paged
               * `/Observation` fetch that feeds this, and the accumulation
               * itself, are work whose result nothing consumes. Wiring the
               * table up to it would surface the mis-keying immediately.
               * `mapKey` names the coercion that already happens rather than
               * changing it.
               */
              const patientKey = mapKey((observation as { patientId?: string }).patientId);
              if (!Object.prototype.hasOwnProperty.call(patientObservations, patientKey)) {
                patientObservations[patientKey] = {
                  ALLERGIES: [],
                  'FOOD-PREFERENCES': [],
                  'FOOD-AVOIDANCE': [],
                  DIAGNOSIS: [],
                };
              }

              // The bucket was just seeded above, and `code` is one of the four
              // the `/Observation` query pins — see `PatientObservationsByCode`.
              patientObservations[patientKey]![
                observation.code as keyof PatientObservationsByCode
              ].push(observation.valueString);
            });
          }
        });
        setPatientObservations(patientObservations);
      },
      (_error) => {},
    );
  };

  const getPatients = (urls: string[]) => {
    MultiFetcher<Bundle>(authInfo.token, ...urls).then(
      (patientsDataset) => {
        // These accumulate into id-keyed maps via the object spreads below, so they are records.
        // Values are `parseFHIRPatient` output, i.e. `FlatPatient`, not `unknown` —
        // stated so the map satisfies the table's `patients` prop without a cast.
        let combinedPatients: Record<string, FlatPatient> = {};
        let combinedRawPatients: Record<string, unknown> = {};
        // Same reachable-`undefined` defect as `getPatientObservations` above, and
        // asserted for the same reason: `!` erases, so the throw is preserved.
        patientsDataset!.forEach((patientsData) => {
          if (patientsData && patientsData.entry && patientsData.entry.length) {
            const tempPatients: Record<string, FlatPatient> = {};
            const tempRawPatients: Record<string, unknown> = {};
            patientsData.entry.forEach((item) => {
              // `resource` is `0..1`. Asserted rather than narrowed: narrowing would
              // skip a malformed entry, silently dropping a member from the roster
              // where today the parse throws.
              tempPatients[item.resource!.id!] = FHIRUtils.parseFHIRPatient(
                item.resource as Patient,
              );
              tempRawPatients[item.resource!.id!] = item.resource;
            });
            combinedPatients = { ...combinedPatients, ...tempPatients };
            combinedRawPatients = {
              ...combinedRawPatients,
              ...tempRawPatients,
            };
            setLoading(false);
          }
        });
        setPatients(combinedPatients);
        setRawPatients(combinedRawPatients);
      },
      (_error) => {},
    );
  };

  const url =
    '?patientOrg=' +
    props.selectedOrg +
    (props.patientId
      ? props.vendor === 'All'
        ? `&patientId=` + props.patientId
        : `&vendorId=` + props.vendor + `&patientId=` + props.patientId
      : props.vendor !== 'All'
        ? `&vendorId=` + props.vendor
        : ``) +
    '&status=draft' +
    (searchId ? '&orderId=' + searchId : '') +
    (startRange
      ? '&createdDate:ge=' +
        moment(startRange).utc().format() +
        (endRange ? '&createdDate:le=' + moment(endRange).utc().add(1, 'days').format() : '')
      : '');

  const {
    data: ordersData,
    error: ordersError,
    mutate: ordersMutate,
    isValidating: datasetLoading,
  } = useSWR<Fetched<FoodOrder[]>, Error>(
    [`/order` + url, authInfo.token],
    FoodFetcher,
    draftOrdersOptions,
  );

  ordersData?.sort((a, b) => {
    // See the SUSPECTED DEFECT note on the identical comparator in
    // `../orders/index.tsx`: an absent `orderDate` yields `NaN` here, which makes the
    // whole sort's ordering inconsistent rather than merely misplacing one row.
    return new Date(b.orderDate!).getTime() - new Date(a.orderDate!).getTime();
  });

  ordersData?.map((order) => {
    const quantity =
      order.orderItems &&
      order.orderItems.length > 0 &&
      _.sum(
        order.orderItems.map((od) => {
          return parseInt(od.quantity);
        }),
      );
    order.quantity = quantity as number;
  });

  // Dead since the member drawer stopped reading it; the assignment is kept so the
  // emitted program still matches upstream. Renaming it would not.
  let flatPatient;
  if (props.patientId) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    flatPatient = props.patient;
  }

  useEffect(() => {
    dispatch({ type: 'CLEAR_FILTERS', data: ordersData });
    setOrdersMessage('');
    setselectedOrderIds([]);
    setselectedOrdersLength(0);
  }, [ordersData]);

  // An absent id opens the drawer on nothing rather than throwing — as it does today.
  const openSelectedPatient = (id?: string) => {
    if (rawPatients) {
      props.onPatientSelected!(rawPatients[mapKey(id)]);
    }
  };

  const resetFilters = () => {
    setStatusValue(null);
    setDateValue(null);
    setdateState('');
    setStartRange('');
    setEndRange('');
    setSearchId('');
    setOrdersMessage('');
    dispatch({ type: 'CLEAR_FILTERS', data: ordersData });
  };

  /**
   * SUSPECTED DEFECT — REPORTED, NOT FIXED. `selectedOrderIds` is declared `string[]`,
   * but `FoodOrder.orderId` is optional, so "select all" over a page containing an
   * order the feed sent without an `orderId` puts `undefined` into the selection.
   *
   * That `undefined` then flows to `markAccepted`, whose filter is
   * `requiredOrderIds.includes(od.orderId)` — which MATCHES every other order that
   * also lacks an `orderId`, and to `FoodUpdater('/order/' + order.orderId...)`,
   * which would address `/order/undefined`. The twin in `../orders/index.tsx` has the
   * same two lines and the same defect; it is already in the typecheck baseline there
   * (TS2345 at its lines 844 and 857).
   *
   * Not guarded here: filtering the absent ids out would silently shrink a
   * select-all the care manager believes covers the page.
   */
  const selectAll = (_e: React.FormEvent<HTMLInputElement>, checkprops: StrictCheckboxProps) => {
    if (checkprops.checked) {
      setselectedOrderIds(
        state.data!.map((d) => {
          return d.orderId;
        }),
      );
      setselectedOrdersLength(state.data!.length);
    } else {
      setselectedOrderIds([]);
      setselectedOrdersLength(0);
    }
  };

  /**
   * `status` stays a `string` rather than the `'new' | 'rejected'` its three call
   * sites actually pass: `DraftOrdersTableProps` declares the sibling hand-offs
   * (`acceptSingleOrder`, `updateDraftOrder`) with a `string` status, and narrowing
   * only this end would make the pair disagree without telling anyone anything new.
   *
   * `selectedOrderIdsTemp` carries `(string | undefined)[]` for the same reason
   * `selectedOrderIds` does; see the note on `selectAll`.
   */
  async function markAccepted(status: string, selectedOrderIdsTemp: (string | undefined)[]) {
    setLoading(true);

    const requiredOrderIds = selectedOrderIdsTemp.length ? selectedOrderIdsTemp : selectedOrderIds;

    const selectedOrders = state.data!.filter((od) => {
      return requiredOrderIds.includes(od.orderId);
    });

    if (status === 'rejected') {
      for (let i = 0; i < selectedOrders.length; i++) {
        const order = selectedOrders[i]!;
        if (order.status && order.status.toLowerCase() === 'draft') {
          setLoading(true);
          await updateDraftOrder(order, status, true);
          await ordersMutate();
          setLoading(false);
        }
      }
    } else {
      const patientIds = selectedOrders.map((order) => order.patientId);

      const ordersUrl: string[] = [];

      // ordersUrl.push(`/food/price?organization=ALL`);
      const foodPriceUrl = [`/food/price?organization=ALL`];
      let foodPricingByVendors: VendorPricingByCode = {};

      setLoading(true);

      await MultiFoodFetcher(authInfo.token, foodPriceUrl as unknown as string).then(
        // eslint-disable-next-line @typescript-eslint/require-await -- upstream writes this callback async; removing it would change the emit
        async (foodPricing) => {
          setLoading(false);
          /**
           * SUSPECTED DEFECT: this read assumes the `/food/price` response is present
           * and non-empty, and neither is guaranteed.
           *
           * `MultiFoodFetcher` resolves `(unknown[] | undefined)[]`: `FoodFetcher`
           * returns `undefined` for an empty endpoint, and an endpoint that matched
           * nothing resolves an empty array. Either makes this throw during a bulk
           * draft-order submission, after `setLoading(false)` has already run — so the
           * spinner clears and the submission dies with no message to the care manager.
           *
           * `foodPriceUrl` is also an ARRAY passed to a `...urls: string[]` rest
           * parameter, so the URL is the stringified array. That is unchanged here.
           */
          foodPricingByVendors = (foodPricing[0]![0]! as VendorPriceRow).vendors!;
        },
      );

      // Orders with no `patientId` all share the single `"undefined"` bucket the
      // subscript coerces them to.
      const patientMEQs: Record<string, number | undefined> = {};

      // Meant only for programs with Contracted Meal Equivalents
      //if (PROGRAMS_MEQ.includes(props.selectedOrg)) {
      const uniquePatients = patientIds.filter(
        (value, index, array) => array.indexOf(value) === index,
      );

      if (uniquePatients && uniquePatients.length) {
        const groups = uniquePatients
          .map((e, i) => {
            return i % 10 === 0 ? uniquePatients.slice(i, i + 10) : null;
          })
          .filter((e): e is (string | undefined)[] => Boolean(e));

        // eslint-disable-next-line @typescript-eslint/no-for-in-array -- see the note on the identical traversal in the `onSuccess` above.
        for (const groupIndex in groups) {
          // if(groups[groupIndex].length >= 30) {
          ordersUrl.push(`/order?patientId:in=${groups[groupIndex]!.join(',')}`);
        }
      }

      setLoading(true);
      await MultiFoodFetcher(authInfo.token, ordersUrl as unknown as string).then(
        // eslint-disable-next-line @typescript-eslint/require-await -- upstream writes this callback async; removing it would change the emit
        async (groupOrders) => {
          setLoading(false);
          groupOrders.forEach((orders) => {
            // if (dataset[3] && dataset[3].length && dataset[3][0].vendors) {
            //   setVendorsFoodPrice(dataset[3][0].vendors);
            // }

            // Fetch Meal Equivalents for all the EXISTING Orders of the patient
            //
            // Every URL in `ordersUrl` is a `/order?patientId:in=…` search, which is what
            // justifies the claim on these rows.
            (orders as FoodOrder[] | undefined)!.forEach((order) => {
              if (Object.prototype.hasOwnProperty.call(patientMEQs, mapKey(order.patientId))) {
                patientMEQs[mapKey(order.patientId)]! += calculateMealEquivalents(
                  order,
                  false,
                  props,
                );
              } else {
                patientMEQs[mapKey(order.patientId)] = calculateMealEquivalents(
                  order,
                  false,
                  props,
                );
              }
            });
          });
        },
      );
      //}

      const errorOrders: string[] = [];
      const acceptedOrders: FoodOrder[] = [];
      // SUSPECTED DEFECT: `pricingCalculator` returns `CalculatedFoodPrice | undefined`
      // (it falls off the end when an order has no errors but a falsy `itemPricingnFees`
      // or `quantity`), so this reassignment can make `validPrice` undefined even though
      // the initialiser here implies it never is. The reads below use `validPrice?.foodPrice`,
      // which is what keeps it from throwing — and what silently records `undefined` pricing.
      let validPrice = {
        foodPrice: 0,
        shippingFees: 0,
        message: '',
        hasErrors: false,
        quantity: 0,
      };
      for (let i = 0; i < selectedOrders.length; i++) {
        const order = selectedOrders[i]!;
        if (order.status && order.status.toLowerCase() === 'draft') {
          // Calculate meal equivalents for draft orders

          if (
            order.mealEquivalents === undefined ||
            !order.mealEquivalents ||
            order.mealEquivalents === 0
          ) {
            validPrice = pricingCalculator(
              order.patientOrg!,
              order.vendorId!,
              order.orderItems!,
              foodPricingByVendors,
              order?.bundleName,
            )!;
            //order.mealEquivalents = validPrice.quantity;
            order['foodPricing'] = validPrice?.foodPrice;
            order['shippingFees'] = validPrice?.shippingFees;
          }

          // Meant only for programs with Contracted Meal Equivalents
          if (PROGRAMS_MEQ.includes(props.selectedOrg!)) {
            // `patientMEQs` holds numbers and `parseInt` declares a `string` parameter:
            // standing TS2345s of the kind `foodUtils.ts` documents. `as string` would
            // claim these are strings when they are not.
            const meqCalc =
              (patientMEQs[mapKey(order.patientId)]
                ? parseInt(patientMEQs[mapKey(order.patientId)] as unknown as string)
                : 0) +
              (order.mealEquivalents ? parseInt(order.mealEquivalents as unknown as string) : 0);
            /*
             * SUSPECTED DEFECT — REPORTED, NOT FIXED. Two faults on one line.
             *
             * Precedence: `>` binds tighter than `?:`, so this reads
             * `(meqCalc > contractsME[org]) ? parseInt(...) : 0` — the ternary is
             * the CONDITION of the `if`, not a fallback for an absent contract.
             * The `if` therefore fires whenever the parsed contract value is
             * non-zero and truthy, which is nearly always, rather than when the
             * meal-equivalent total actually exceeds it. The intended
             * `meqCalc > (contractsME[org] ?? 0)` is not what runs.
             *
             * Absence: `contractsME` is optional and its subscript is an order's
             * own `patientOrg`, so both the prop and the entry can be missing;
             * `parseInt(undefined)` is `NaN`, and the message pushed below would
             * then read "…/undefined" to the care manager.
             */
            if (
              meqCalc > (props.contractsME![mapKey(order.patientOrg)]! as number)
                ? parseInt(props.contractsME![mapKey(order.patientOrg)] as string)
                : 0
            ) {
              errorOrders.push(
                order.orderId +
                  ' => ' +
                  meqCalc +
                  '/' +
                  props.contractsME![mapKey(order.patientOrg)],
              );
              continue;
            }
          }
          //body['mealEquivalents'] = orderQuantity;
          acceptedOrders.push(order);
        }
      }
      if (errorOrders.length) {
        setHideMessage(false);
        setOrdersMessage('Exceeding meal equivalents for the orders:  ' + errorOrders.join(', '));
      } else {
        setLoading(true);
        // Async callback passed where a void return is expected, as upstream writes it; wrapping it
        // would emit a new function.
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        acceptedOrders.forEach(async (order, index) => {
          await updateDraftOrder(order, status, true);
          // wait until all the orders are accepted
          if (index === acceptedOrders.length - 1) {
            await ordersMutate();
            setLoading(false);
          }
        });
      }
    }
  }

  /**
   * SUSPECTED DEFECT — REPORTED, NOT FIXED. `order.orderId.toString()` below is
   * unguarded and `orderId` is optional, so an order the feed sent without one throws.
   * Not guarded: a `?.` would PUT to `/order/undefined`, silently updating nothing.
   */
  async function updateDraftOrder(order: FoodOrder, status: string, multiOrderFlag = false) {
    setLoading(true);
    const orderUpdate = {
      patientId: order.patientId,
      docId: order.orderId,
      status: status,
      acceptedBy: authInfo.practitionerName,
      acceptedDate: moment().utc().format(),
      orderDate: moment().utc().format(),
      lastUpdated: moment().utc().format(),
      lastUpdatedBy: authInfo.practitionerName,
      mealEquivalents: order.mealEquivalents,
      foodPricing: order?.foodPricing,
      shippingFees: order?.shippingFees,
    };

    await FoodUpdater(`/order/` + order.orderId!.toString(), authInfo.token, orderUpdate).then(
      () => {},
    );
    if (!multiOrderFlag) {
      await ordersMutate();
      setLoading(false);
    }
  }

  // Declared async so callers get a promise; removing async changes the return type from a promise
  // to a value.
  // eslint-disable-next-line @typescript-eslint/require-await
  async function acceptSingleOrder(order: FoodOrder, status: string) {
    setselectedOrderIds([order.orderId]);
    // Fire-and-forget as written upstream: awaiting would serialise it, .catch() would add a
    // handler. Neither erases.
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    markAccepted(status, [order.orderId]);
  }

  /**
   * SUSPECTED DEFECT — REPORTED, NOT FIXED. The `if (orderItems !== null)` guard below
   * is DEAD, and the error message it gates can never be shown.
   *
   * A previous note here (and its twin in `food/orders/index.tsx`) said
   * `buildOrderItems` "returns undefined when no bundle item matches". It does not:
   * its body is `items.filter(…)`, which returns `[]` when nothing matches and THROWS
   * when `items` is absent. There is no path on which it produces `undefined` or
   * `null`. So after the assignment `orderItems` is always an array, the guard is
   * always true, and `setOrdersUpdateError(true)` in the `else` arm is unreachable —
   * a failed bundle swap reports nothing to the care manager.
   *
   * What CAN go wrong is the absence one level up: `foodItemsDataset` is
   * `unknown[] | undefined`, because `MultiFoodFetcher` resolves `undefined` for an
   * endpoint that returned nothing. That makes `items.filter` throw inside the `.then`,
   * which rejects the promise nothing here awaits — so the spinner set by
   * `setLoading(true)` never clears.
   *
   * Not fixed: hoisting or widening the guard would turn a visible hang into a
   * silently skipped price update on the alternate-bundle swap.
   */
  async function updateAlternateOption(
    order: FoodOrder,
    alternateFoodOptions: string[],
    selectedAlternate: string,
  ) {
    setLoading(true);
    /*
     * An ARRAY, not a record — `buildOrderItems` returns `items.filter(…)`, and
     * `pricingCalculator` declares its third parameter `PricedOrderItem[]` and reads
     * `.length` off it. Declared `Record<string, unknown> | null` before, which
     * neither the producer nor the consumer ever matched.
     *
     * The intersection is not decoration: the two `orderItems['foodPricing'] = …`
     * writes below really do hang NAMED properties off that array. They are readable
     * in-process — which is what lines further down rely on — but `JSON.stringify`
     * drops named properties from an array, so they do NOT survive into the
     * `FoodUpdater` body. The payload carries them only because it also copies them
     * to its own top-level `foodPricing`/`shippingFees` keys.
     */
    let orderItems:
      | (FoodItem[] & { foodPricing?: number | undefined; shippingFees?: number | undefined })
      | null = null;

    // NOTE: order of urls in this array matter!
    const urls = [`/food/bundle-item?bundleId=${selectedAlternate}`];

    const itemUrl = `/food/item?vendorId=${order.vendorId}`;

    urls.push(itemUrl);

    if (order?.patientOrg) {
      urls.push(`/food/price?organization=ALL`);
    }

    await MultiFoodFetcher(authInfo.token, ...urls).then(async (dataset) => {
      // Positional, per the "order of urls matter" note above.
      const bundleItemsDataset = dataset[0] as FoodItem[] | undefined;
      const foodItemsDataset = dataset[1] as FoodItem[] | undefined;

      /* Setting Bundle Items Data    */
      setBundleItemsData(bundleItemsDataset);

      /* Setting Food Items Data    */
      setItemsData(foodItemsDataset);

      orderItems = await buildOrderItems(foodItemsDataset!, bundleItemsDataset);

      // `MultiFoodFetcher` resolves each URL to `unknown[] | undefined`, so the
      // `/food/price` row arrives untyped. `VendorPriceRow` names the one field read
      // off it; the cast erases, leaving upstream's three subscripts as written.
      if (
        dataset[2] &&
        dataset[2]?.length &&
        (dataset[2][0] as VendorPriceRow | undefined)?.vendors
      ) {
        setVendorsFoodPrice(
          (dataset[2][0] as VendorPriceRow | undefined)?.vendors as VendorPricingByCode,
        );
        const validPrice = pricingCalculator(
          order?.patientOrg as string,
          props.vendor!,
          orderItems,
          (dataset[2][0] as VendorPriceRow | undefined)?.vendors as VendorPricingByCode,
          selectedAlternate,
        );
        orderItems['foodPricing'] = validPrice?.foodPrice;
        orderItems['shippingFees'] = validPrice?.shippingFees;
      }

      if (orderItems !== null) {
        const alternateOptions = alternateFoodOptions.filter((item) => item !== selectedAlternate);
        const orderUpdate = {
          patientId: order.patientId,
          docId: order.orderId,
          orderName: selectedAlternate,
          bundleId: selectedAlternate,
          bundleName: selectedAlternate,
          alternateFoodOptions: alternateOptions,
          orderItems: orderItems,
          lastUpdated: moment().utc().format(),
          lastUpdatedBy: authInfo.practitionerName,
          foodPricing: orderItems?.['foodPricing'],
          shippingFees: orderItems?.['shippingFees'],
        };

        await FoodUpdater(`/order/` + order.orderId!.toString(), authInfo.token, orderUpdate).then(
          () => {},
        );
        // Fire-and-forget as written upstream: awaiting would serialise it, .catch() would add a
        // handler. Neither erases.
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        ordersMutate();
        setLoading(false);
        setOrdersUpdateError(false);
      } else {
        setOrdersUpdateError(true);
        setLoading(false);
      }
    });
  }

  /**
   * `items` is required although the dataset element it comes from is optional:
   * `items.filter` is the first thing this does, so an absent `/food/item` response
   * throws. That is the absence in the defect note on `updateAlternateOption`, stated
   * as a required parameter rather than hidden behind a `?.` that would swap the
   * throw for a silently empty swap.
   */
  // Declared async so callers get a promise; removing async changes the return type from a promise
  // to a value.
  // eslint-disable-next-line @typescript-eslint/require-await
  async function buildOrderItems(
    items: FoodItem[],
    bundleItemsData: FoodItem[] | undefined,
  ): Promise<FoodItem[]> {
    const preparedOrderItems = items.filter((item) => {
      let itemPayload: FoodItem | undefined;
      bundleItemsData?.forEach((bundleItem) => {
        if (bundleItem.itemId === item.itemId) {
          item.quantity = bundleItem.quantity;
          itemPayload = item;
        }
      });
      return itemPayload;
    });
    return preparedOrderItems;
  }

  function addExportData(
    _e: React.FormEvent<HTMLInputElement>,
    checkprops: StrictCheckboxProps,
    orderId?: string,
  ) {
    let temp = selectedOrderIds;
    if (checkprops.checked) {
      temp.push(orderId);
    } else {
      temp = temp.filter((o) => {
        return o !== orderId;
      });
    }
    setselectedOrderIds(temp);
    setselectedOrdersLength(temp.length);
  }

  // Unreachable: `cancelConfirm` is never set true, so the Confirm this is wired to
  // never opens and no control invokes it. Dead upstream too; kept as ported.
  function cancelOrders() {
    setWorking(true);
    setHideMessage(false);
    setWorkingOn('Cancelling');
    const temp =
      ordersData &&
      ordersData.filter((od) => {
        return selectedOrderIds.includes(od.orderId);
      });
    setOrdersMessage('Cancelling ' + temp!.length.toString() + ' orders...');
    temp!.map((order) => {
      if (props.vendor === 'SB') {
        // Only the rejection is dropped, not the result: the `.then` clears the cancelling
        // message and it is skipped, so a failure leaves it up. Kept as upstream writes it —
        // awaiting would serialise it and .catch() would add a handler, and neither erases.
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        FoodDelete(
          `/order/sb/` + order.vendorOrderId + `?orderId=` + order.orderId,
          authInfo.token,
        ).then((response) => {
          if (response === '') {
            setOrdersMessage('Cancellation Successful!');
          } else {
            setOrdersMessage(response.title + ': ' + response.detail);
          }
          setWorking(false);
        });
      } else {
        const payload: {
          status?: string;
          cancelledDate?: string;
          cancelledBy?: string;
          lastUpdated?: string;
          lastUpdatedBy?: string;
        } = {};
        payload['status'] = 'CANCELLED';
        payload['cancelledDate'] = new Date().toISOString();
        payload['cancelledBy'] = authInfo.practitionerName;
        payload['lastUpdated'] = new Date().toISOString();
        payload['lastUpdatedBy'] = authInfo.practitionerName;
        // Only the rejection is dropped, not the result: the `.then` clears the cancelling
        // message and it is skipped, so a failure leaves it up. Kept as upstream writes it —
        // awaiting would serialise it and .catch() would add a handler, and neither erases.
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        FoodUpdater(`/order/` + order.orderId, authInfo.token, payload).then((response) => {
          if (response.statusCode === '200') {
            setOrdersMessage('Order Cancelled Successfully!');
          } else {
            setOrdersMessage('Cancellation Error - ' + response.message);
          }
          // Fire-and-forget as written upstream: awaiting would serialise it, .catch() would add a
          // handler. Neither erases.
          // eslint-disable-next-line @typescript-eslint/no-floating-promises
          ordersMutate();
          setCancelConfirm(false);
          setWorking(false);
        });
      }
    });
  }
  /**
   * `e` is `{ target }` rather than a React event type: the arms read
   * `e.target.textContent` (a Semantic dropdown item), `e.target.valueAsNumber` and
   * `e.target.value` (a number/text input), and no single DOM element carries all
   * three. This names what the arms actually read.
   *
   * `attributeName` stays a `string`, NOT `keyof FoodOrder`. Narrowing it that far
   * would be an overclaim about the writes rather than a check of them: TypeScript
   * cannot narrow a write through a union key, so every `temp[attributeName] = …`
   * below resolves its target to `never` and the whole body becomes errors that say
   * nothing about the product. The draft accumulator it writes into is typed instead,
   * which is where a wrong field name would actually show up.
   */
  const _handleChange = (
    e: { target?: (Partial<HTMLInputElement> & { textContent?: string | null }) | null },
    attributeName: string,
    previous?: OrderNote[],
  ) => {
    const temp: Record<string, unknown> = orderDetails;
    const value = e.target && e.target.textContent;
    if (attributeName === 'status' && (value === 'NEW' || value === 'EXPORTED')) {
      temp[attributeName] = value.toLowerCase();
    } else if (attributeName === 'bundleQuantity') {
      temp[attributeName] = e.target && e.target.valueAsNumber && e.target.valueAsNumber;
      temp['quantity'] = temp[attributeName];
    } else if (
      attributeName === 'foodPricing' ||
      attributeName === 'shippingFees' ||
      attributeName === 'estimatedDeliveryDate'
    ) {
      temp[attributeName] = e.target && e.target.value;
    } else if (attributeName === 'orderNotes') {
      let notepayload: OrderNote[] = [];
      if (previous) {
        notepayload = [...previous];
      }
      const currentPayload: OrderNote = {
        author: practitionerName,
        time: moment().utc().format(),
        text: e.target! && e.target.value!,
      };
      notepayload.push(currentPayload);
      temp[attributeName] = [...notepayload];
    } else {
      temp[attributeName] = value;
    }
    if (temp['status'] && invoiceStatusList.includes((temp['status'] as string).toLowerCase())) {
      temp['invoiceReady'] = '1';
    }
    setOrderDetails(temp);
  };
  const _handleEstimateDeliveryDateChange = (
    _e: React.SyntheticEvent,
    estimatedDeliveryDate: { value?: string },
  ) => {
    const temp = orderDetails;
    temp['estimatedDeliveryDate'] = moment(estimatedDeliveryDate.value).format('YYYY-MM-DD');
    setOrderDetails(temp);
  };
  // `searchProps.value` is Semantic's `InputOnChangeData['value']`, a `string`.
  const handleSearchById = (_e: React.SyntheticEvent, searchProps: InputOnChangeData) => {
    setSearchId(searchProps.value);
  };
  /*
   * `value` is the `DatesRangeInput`'s "YYYY-MM-DD - YYYY-MM-DD" string, split on the
   * separator. `noUncheckedIndexedAccess` makes both halves `string | undefined`,
   * which is true of runtime: before the second date is picked the widget emits a
   * half-open range ("2026-01-01 - ") and `temp[1]` is the empty string, while a
   * cleared field yields no separator at all and `temp[1]` is genuinely absent.
   * `setEndRange` then receives `undefined` against a `string` state.
   */
  const handleChangeDate = (
    _event: React.SyntheticEvent,
    { name: _name, value }: { name?: string; value: string },
  ) => {
    setdateState(value);
    const temp = value.split(' - ');
    setStartRange(temp[0] as string);
    setEndRange(temp[1] as string);
  };
  const [state, dispatch] = React.useReducer(Reducer, {
    sortcolumn: null,
    data: ordersData && ordersData,
    direction: null,
  });
  // SUSPECTED DEFECT — REPORTED, NOT FIXED. Same as `../orders/index.tsx`: the
  // reducer writes `sortcolumn`, nothing writes `column`, so `column={column}`
  // on the `<DraftOrdersTable>` render below is always `undefined` and no header
  // ever shows its sort caret. Left as-is because renaming it would turn the
  // carets on, which is a visible behaviour change.
  const { data, direction } = state;
  const column: string | undefined = undefined;
  const _selectedOrder =
    selectedOrdersLength === 1 &&
    ordersData &&
    _.filter(ordersData, { orderId: selectedOrderIds[0] })[0];
  return (
    <div style={{ minHeight: '65vh' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Form autoComplete="off">
          <Form.Group>
            <Form.Input
              placeholder="Search by ID"
              icon={<Icon name="search" onClick={handleSearchById} />}
              value={searchId}
              onChange={(e, searchProps) => setSearchId(searchProps.value)}
              name="searchId"
            />
            <DatesRangeInput
              name="datesRange"
              placeholder="Filter by Date Range"
              value={dateState}
              iconPosition="left"
              dateFormat="YYYY-MM-DD"
              onChange={handleChangeDate}
            />

            <Form.Button content="Clear" color="blue" onClick={resetFilters} />
          </Form.Group>
        </Form>
        <span style={{ float: 'right' }}>
          <span className="btn-group">
            {selectedOrdersLength > 0 && (
              <>
                <Confirm
                  open={cancelConfirm}
                  onConfirm={cancelOrders}
                  confirmButton={<Button content="Yes, Cancel Order" negative />}
                  cancelButton={<Button content="Do not Cancel" positive />}
                  onCancel={() => setCancelConfirm(false)}
                />

                <Button
                  icon="handshake outline"
                  content="Accept"
                  color="green"
                  onClick={() => markAccepted('new', [])}
                />
                <Button
                  icon="cancel"
                  content="Reject"
                  color="red"
                  onClick={() => markAccepted('rejected', [])}
                />
              </>
            )}
          </span>
        </span>
      </div>
      <div>
        {((!ordersData && !ordersError) || loading || datasetLoading) && (
          <Loader active inline="centered" />
        )}
        {data && data.length === 0 && <Message warning header="Info" content="0 Items found!" />}
        {ordersError && (
          <Message header="Oops!. Something went wrong.." content={ordersError.message} negative />
        )}
        {ordersUpdateError && (
          <Message
            header="Oops!. Something went wrong.."
            content="Error updating draft order..Please check logs"
            negative
          />
        )}

        {data && data.length > 0 && (
          <>
            {ordersMessage.length > 0 && (
              <>
                <Message
                  icon
                  color="red"
                  negative={ordersMessage.toLowerCase().includes('error')}
                  positive={ordersMessage.toLowerCase().includes(' successful')}
                  hidden={hideMessage}
                >
                  {working && (
                    <>
                      <Icon name="circle notched" loading />
                      <Message.Content>
                        <Message.Header>Just one second</Message.Header>
                        {workingon}{' '}
                        {selectedOrderIds.length > 1
                          ? selectedOrderIds.length.toString() + ' orders'
                          : 'the order'}
                        ...
                      </Message.Content>
                    </>
                  )}
                  {!working && ordersMessage.length > 0 && (
                    <>
                      <Message.Content>
                        <Message.Header>
                          {ordersMessage.toLowerCase().includes(' successful')
                            ? workingon + ' Success'
                            : workingon + ' Error'}
                        </Message.Header>
                        {ordersMessage}
                      </Message.Content>
                      <Icon name="close" onClick={() => setHideMessage(true)} />
                    </>
                  )}
                </Message>
              </>
            )}
            <DraftOrdersTable
              patientId={props.patientId!}
              org={props.patient! && props.patient.org}
              vendor={props.vendor!}
              data={data}
              patients={patients && patients}
              openSelectedPatient={openSelectedPatient}
              selectedOrderIds={selectedOrderIds}
              column={column!}
              direction={direction}
              addExportData={addExportData}
              selectAll={selectAll}
              dispatch={dispatch}
              markAccepted={markAccepted}
              updateDraftOrder={updateDraftOrder}
              updateAlternateOption={updateAlternateOption}
              patientObservations={patientObservations}
              acceptSingleOrder={acceptSingleOrder}
            />
          </>
        )}
      </div>
    </div>
  );
}
export default DraftOrders;
